import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { NauticusLogo } from "@/components/NauticusLogo";
import { SBALogo } from "@/components/SBALogo";
import { PoweredByFooter } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowRight, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const isSBA = new URLSearchParams(window.location.search).get("brand") === "sba";
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data?.message || "If an account exists, a reset link is on its way.");
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-mist-100">
      <div className="w-full max-w-md">
        <div className="mb-8">{isSBA ? <SBALogo /> : <NauticusLogo />}</div>
        <h1 className="font-display text-3xl font-bold text-navy-900 tracking-tight">Reset your password</h1>
        <p className="text-muted-foreground mt-2 mb-8">
          Enter the email on your account and we'll send you a secure link to set a new password.
        </p>

        {error && (
          <Alert variant="destructive" className="mb-5" data-testid="forgot-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {sent ? (
          <Alert className="mb-5 bg-white border-mist-200" data-testid="forgot-sent">
            <MailCheck className="w-4 h-4 text-gold-dark" />
            <AlertDescription>{msg}</AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="font-semibold">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" className="mt-1.5 h-11 bg-white" data-testid="forgot-email" />
            </div>
            <Button type="submit" disabled={loading} data-testid="forgot-submit"
              className="w-full h-11 bg-navy-800 hover:bg-navy-700 text-white rounded-full font-bold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset link <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </form>
        )}

        <p className="text-sm text-muted-foreground mt-6">
          <Link to={isSBA ? "/login?brand=sba" : "/login"} className="text-gold-dark font-semibold hover:underline" data-testid="forgot-back-login">
            Back to sign in
          </Link>
        </p>
        <PoweredByFooter className="mt-10" />
      </div>
    </div>
  );
}
